// App.js
import React, { useState, useEffect, useRef } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import './App.css';

// Preload fart sounds
const fartSounds = (() => {
  const sounds = {};
  for (let i = 1; i <= 9; i++) {
    const audio = new Audio(process.env.PUBLIC_URL + `/sounds/fart${i}.mp3`);
    audio.preload = 'auto';
    sounds[i] = audio;
  }
  return sounds;
})();

// Helper function to sleep for ms milliseconds.
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Generate a random sequence based on the current round.
const generateSequence = (round) => {
  const length = 3 + round; // Increase length with round.
  return Array.from({ length }, () => Math.floor(Math.random() * 9) + 1);
};

// Helper function to format seconds into mm:ss.
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

function App() {
  const { connected } = useWallet();
  const { connection } = useConnection();

  // Overlay state & live countdown (60 minutes = 3600 seconds)
  const [showLiveOverlay, setShowLiveOverlay] = useState(true);
  const [liveCountdown, setLiveCountdown] = useState(60 * 60);

  // Countdown timer for the overlay
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setShowLiveOverlay(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Game state
  const [phase, setPhase] = useState('roundDelay'); // 'roundDelay', 'betting', 'sequence', 'input', 'eliminated'
  const [countdown, setCountdown] = useState(10);
  const [round, setRound] = useState(1);
  const [targetSequence, setTargetSequence] = useState([]);
  const [playerSequence, setPlayerSequence] = useState([]);
  const [announcement, setAnnouncement] = useState('New round starting soon...');
  const [isBetPlaced, setIsBetPlaced] = useState(false);
  const [prizePool, setPrizePool] = useState(0);
  const [balance, setBalance] = useState(100);

  // Bet amount state
  const [betAmount, setBetAmount] = useState(10);

  // Simulated bets and leaderboard state
  const [simulatedBets, setSimulatedBets] = useState([]);
  const [leaderboard, setLeaderboard] = useState({});

  // Elimination waiting countdown state
  const [eliminationDelay, setEliminationDelay] = useState(0);
  const [waitingCountdown, setWaitingCountdown] = useState(0);

  // Refs for intervals and auto-scrolling
  const betsIntervalRef = useRef(null);
  const betsContainerRef = useRef(null);
  // Ref to hold the current betting countdown (so we can stop bet generation near end)
  const countdownRef = useRef(countdown);
  useEffect(() => {
    countdownRef.current = countdown;
  }, [countdown]);

  // Countdown timer for phases: roundDelay, betting, and input.
  useEffect(() => {
    let timer = null;
    if (['roundDelay', 'betting', 'input'].includes(phase)) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            if (phase === 'roundDelay') {
              // Transition to betting phase.
              setPhase('betting');
              setCountdown(10);
              setAnnouncement('Betting window is open! Place your bet.');
            } else if (phase === 'betting') {
              // Betting period over → lock bets.
              setAnnouncement('Bets locked, round starting...');
              // Delay 3 seconds before moving to sequence phase.
              setTimeout(() => {
                setPhase('sequence');
              }, 3000);
            } else if (phase === 'input') {
              // Input phase expired → elimination.
              setAnnouncement('Time is up! You are eliminated.');
              setEliminationDelay(0);
              setPhase('eliminated');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [phase]);

  // When phase is "sequence", generate and play the sequence.
  useEffect(() => {
    if (phase === 'sequence') {
      const seq = generateSequence(round);
      setTargetSequence(seq);
      setAnnouncement('Watch the sequence...');
      const playSequence = async () => {
        for (const num of seq) {
          const btn = document.querySelector(`.fart-key[data-num="${num}"]`);
          if (btn) btn.classList.add('highlight');
          const sound = new Audio(fartSounds[num].src);
          sound.play();
          await sleep(1000);
          if (btn) btn.classList.remove('highlight');
          await sleep(300);
        }
        setPlayerSequence([]);
        setAnnouncement('Now repeat the sequence!');
        setCountdown(15);
        setPhase('input');
      };
      playSequence();
    }
  }, [phase, round]);

  // Simulate incoming bets during the betting phase.
  useEffect(() => {
    if (phase === 'betting') {
      setSimulatedBets([]);
      setLeaderboard({});
      betsIntervalRef.current = setInterval(() => {
        // Do not generate new bets in the last 2 seconds.
        if (countdownRef.current <= 2) return;
        const users = ["Player1", "CoolCat47", "FartMaster3000", "GamerGal", "BetKing", "CryptoAce"];
        const randomUser = users[Math.floor(Math.random() * users.length)];
        const randomBet = Math.floor(Math.random() * 500) + 10;
        const betMsg = `${randomUser} bet $${randomBet}`;
        setSimulatedBets((prev) => [...prev, betMsg]);
        setLeaderboard((prev) => {
          const newLb = { ...prev };
          newLb[randomUser] = (newLb[randomUser] || 0) + randomBet;
          return newLb;
        });
      }, Math.random() * 1500 + 500);
      return () => clearInterval(betsIntervalRef.current);
    }
  }, [phase]);

  // Auto-scroll the bets container when new bets are added.
  useEffect(() => {
    if (betsContainerRef.current) {
      betsContainerRef.current.scrollTop = betsContainerRef.current.scrollHeight;
    }
  }, [simulatedBets]);

  // Handle user's button press during the input phase.
  const handleFartPress = (num) => {
    if (phase !== 'input') return;
    const sound = new Audio(fartSounds[num].src);
    sound.play();
    const newSeq = [...playerSequence, num];
    setPlayerSequence(newSeq);
    const idx = newSeq.length - 1;
    if (newSeq[idx] !== targetSequence[idx]) {
      setEliminationDelay(countdown);
      setAnnouncement('Wrong sequence! You are eliminated.');
      setPhase('eliminated');
      return;
    }
    if (newSeq.length === targetSequence.length) {
      setAnnouncement('Correct! Preparing next round...');
      setTimeout(() => {
        setRound((prev) => prev + 1);
        setCountdown(10);
        setPhase('roundDelay');
        setAnnouncement(`Round ${round + 1} starting soon...`);
        setIsBetPlaced(false);
        setPrizePool(0);
      }, 1500);
    }
  };

  // Betting handler.
  const handlePlaceBet = (amount) => {
    if (phase !== 'betting' || isBetPlaced) return;
    setIsBetPlaced(true);
    console.log('Bet placed:', amount);
    setBalance((prev) => prev - amount);
    setPrizePool((prev) => prev + amount);
    setAnnouncement(`Bet placed: ${amount}`);
  };

  // When eliminated, start a waiting countdown (10 + leftover input seconds).
  useEffect(() => {
    if (phase === 'eliminated') {
      const totalDelay = 10 + eliminationDelay;
      setWaitingCountdown(totalDelay);
      const interval = setInterval(() => {
        setWaitingCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      const timer = setTimeout(() => {
        setRound(1);
        setCountdown(10);
        setPhase('roundDelay');
        setAnnouncement('New round starting soon...');
        setIsBetPlaced(false);
        setPrizePool(0);
      }, totalDelay * 1000);
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [phase, eliminationDelay]);

  // Prepare sorted leaderboard array.
  const leaderboardArray = Object.entries(leaderboard).sort((a, b) => b[1] - a[1]);

  return (
    <div className="crt-screen">
      {/* Background Music */}
      <audio
        src={process.env.PUBLIC_URL + '/fartmusic.mp3'}
        autoPlay
        loop
      />

      {/* Live Overlay with Countdown */}
      {showLiveOverlay && (
        <div className="live-overlay">
           <h2>Game will be live in:</h2>
           <p> {formatTime(liveCountdown)}mins</p>
          <p>Note: you have to have a minimum of 100k farts to play in the casino</p><p>
            ca: tbh
          </p>
        </div>
      )}

      <div className="window">
        {/* Wallet Section */}
        <div className="wallet-section">
          <WalletMultiButton className="wallet-connect-btn" />
          {connected && (
            <div className="wallet-balance">
              Balance: <span>{balance} $FARTROYALE</span>
            </div>
          )}
        </div>

        {/* Betting Section */}
        <div className="bet-section">
          <input
            type="number"
            min="1"
            placeholder="Enter bet amount"
            className="bet-input"
            disabled={phase !== 'betting'}
            value={betAmount}
            onChange={(e) => setBetAmount(Number(e.target.value))}
          />
          <button
            onClick={() => handlePlaceBet(betAmount)}
            className="place-bet-btn"
            disabled={phase !== 'betting' || isBetPlaced}
          >
            Place Bet ({betAmount})
          </button>
        </div>

        {/* Status / Announcement Box */}
        <div className="status-box">
          <div className="round-info">
            Round: <span>{round}</span>
          </div>
          <div className="announcement">{announcement}</div>
          {phase === 'roundDelay' && <div className="timer">Round starts in: {countdown}s</div>}
          {phase === 'betting' && <div className="timer">Betting window: {countdown}s</div>}
          {phase === 'input' && <div className="timer">Time left to input: {countdown}s</div>}
          {phase === 'eliminated' && <div className="timer">Waiting: {waitingCountdown}s</div>}
        </div>

        {/* Fartboard Grid */}
        <div className="fartboard-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              className="fart-key"
              data-num={num}
              onClick={() => handleFartPress(num)}
            >
              {num}
            </button>
          ))}
        </div>

        {/* Elimination Overlay */}
        {phase === 'eliminated' && (
          <div className="eliminated-overlay">
            <h2>You lost 😢💨</h2>
            <p>{announcement}</p>
            <p>Waiting: {waitingCountdown}s</p>
          </div>
        )}

        {/* Combined Bets & Leaderboard Section */}
        <div className="bets-leaderboard-container">
          <div className="all-bets">
            <h3>All Bets</h3>
            {simulatedBets.length === 0 ? (
              <p>No bets yet.</p>
            ) : (
              simulatedBets.map((bet, index) => <p key={index}>{bet}</p>)
            )}
          </div>
          <div className="leaderboard">
            <h3>Leaderboard</h3>
            {leaderboardArray.length === 0 ? (
              <p>No bets yet.</p>
            ) : (
              <ol>
                {leaderboardArray.map(([user, total], index) => (
                  <li key={index}>
                    {user}: ${total}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
